<script setup lang="ts">
import { literal } from 'zod'

const { defineOgMeta } = useSeoAttributes()

const route = useRoute()

const pageMeta = usePageMeta()
const projects = useProjects()
const {
  public: { baseUrl },
} = useRuntimeConfig()

// Fetch data for meta, list, listIndustry, and projectSlug asynchronously
const [{ data: meta }, { pending, data: projectList }, { data: projectsByIndustry }, { data: projectBySlug }] =
  await Promise.all([
    useAsyncData('projects-meta', async () => pageMeta.fetchByPath('/projects')),
    useAsyncData('projects', async () => projects.fetch()),
    useAsyncData(`project-industry-${route.params.industry}`, async () =>
      projects.fetch(route.params.industry as string),
    ),
    useAsyncData(`project-slug-${route.params.industry}`, async () =>
      projects.fetchBySlug(route.params.industry as string),
    ),
  ])

const dataList = ref(
  projectsByIndustry.value?.industry !== undefined ? projectsByIndustry.value : projectList.value || null,
)

if (
  projectBySlug.value?.project &&
  !dataList.value?.industry &&
  !projectList.value?.industry &&
  route.params.industry
) {
  navigateTo(`/projects/${projectBySlug.value.project.industries[0].slug}/${projectBySlug.value.project.slug}`)
}

if (!projectList && !projectsByIndustry) {
  throw showError({
    statusCode: 404,
  })
}

if (meta.value) {
  useHead(() => ({
    title: meta.value?.title || '',
    titleTemplate: '%s | Webamboos',
    meta: [...defineOgMeta(meta.value)],
    link: [{ rel: 'canonical', href: `${baseUrl}/projects/${route.params.industry}` }],
  }))
}

const breadcrumbs = [
  { href: '/projects', label: 'Our Work' },
  { href: `/projects/${route.params.industry}`, label: projectsByIndustry.value?.industry?.name || '' },
]
</script>

<template>
  <Loader :pending="pending">
    <Container class="screen-content flex-1 px-0 pb-32">
      <Breadcrumbs :items="breadcrumbs" class="pl-8 pt-20 md:pl-16" />

      <LayoutPageHeader :title="dataList!.industry ? dataList!.industry.name : $t('header.nav.projects')" />
      <section v-if="dataList" class="mt-8 flex flex-col gap-y-4 md:gap-y-20">
        <ProjectPreview
          v-for="(project, index) of dataList.projects"
          :key="project.slug"
          :project="project"
          :industry="dataList.industry"
          :is-mirrored="index % 2 !== 0"
        />
      </section>
    </Container>
  </Loader>
</template>
